import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard, { toneBadge } from '@/components/feature/ArticleCard';
import CategoryArticles, { type CategoryLayout } from '@/pages/category/components/CategoryArticles';
import NotFound from '@/pages/NotFound';
import { SITE } from '@/data/site';
import { categories, getCategory } from '@/data/categories';
import { articles, getArticlesByCategory } from '@/data/articles';

const LAYOUT_BY_CATEGORY: Record<string, CategoryLayout> = {
  'yule-redian': 'grid',
  'reju-zong': 'list',
  'wangluo-regeng': 'timeline',
  'guquan-guancha': 'grouped',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategory(slug) : undefined;

  if (!category) {
    return <NotFound />;
  }

  const list = getArticlesByCategory(category.slug);
  const layout = LAYOUT_BY_CATEGORY[category.slug] ?? 'grid';
  const others = articles.filter((a) => a.category !== category.slug).slice(0, 4);
  const hotInCategory = list.slice(0, 4);
  const path = `/category/${category.slug}`;
  const pageTitle = `${category.name} - ${category.tagline} | ${SITE.name}`;

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: pageTitle,
      url: `${SITE.domain}${path}`,
      description: category.description,
      inLanguage: 'zh-CN',
      isPartOf: { '@type': 'WebSite', url: SITE.domain },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: SITE.domain },
        { '@type': 'ListItem', position: 2, name: category.name, item: `${SITE.domain}${path}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: category.name,
      numberOfItems: list.length,
      itemListElement: list.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: a.title,
        url: `${SITE.domain}/article/${a.slug}`,
      })),
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={pageTitle}
        description={category.description}
        keywords={`${category.name},${category.tagline},吃瓜网`}
        path={path}
        schema={schema}
      />

      {/* 栏目头部 */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="max-w-container mx-auto px-4 md:px-8 py-9 md:py-12">
          <Breadcrumbs
            items={[{ label: '首页', path: '/' }, { label: category.name }]}
          />
          <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${toneBadge[category.accent]}`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={category.icon} />
                </span>
                {category.shortName}栏目
              </span>
              <h1 className="mt-4 font-heading text-3xl md:text-4xl font-black text-foreground-950 tracking-tight">
                {category.name}
              </h1>
              <p className="mt-3 text-base leading-8 text-foreground-600">
                {category.description}
              </p>
            </div>
            <div className="shrink-0 text-sm text-foreground-500 md:text-right">
              <p className="font-heading text-3xl font-bold text-primary-500">{list.length}</p>
              <p className="mt-1">篇长文内容</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-container mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* 左侧栏目导航 */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-6">
              <nav className="rounded-lg border border-background-200 bg-background-50 p-5" aria-label="栏目导航">
                <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-4">
                  全部栏目
                </h2>
                <ul className="space-y-1">
                  {categories.map((c) => {
                    const active = c.slug === category.slug;
                    return (
                      <li key={c.slug}>
                        <Link
                          to={`/category/${c.slug}`}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                            active
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-foreground-700 hover:bg-background-100'
                          }`}
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className={c.icon} />
                          </span>
                          {c.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-2">
                  栏目导读
                </h2>
                <p className="text-sm leading-7 text-foreground-600">{category.intro}</p>
              </div>
            </div>
          </aside>

          {/* 内容区 */}
          <div className="lg:col-span-9">
            <CategoryArticles articles={list} layout={layout} accent={category.accent} />

            {/* 本栏目热读 */}
            {hotInCategory.length > 1 && (
              <div className="mt-14">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-bookmark-3-line" />
                  </span>
                  本栏目值得一读
                </h2>
                <div className="rounded-lg border border-background-200 bg-background-50 px-5">
                  {hotInCategory.map((a, i) => (
                    <ArticleCard key={a.slug} article={a} variant="compact" index={i} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 其他栏目推荐 */}
      <section className="max-w-container mx-auto px-4 md:px-8 pb-6">
        <div className="border-t border-background-200 pt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">
            换个角度看热闹
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}